import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tabs, Button, Icon } from '../ui';
import { DARSHAN_TEMPLES } from '../../data/darshan';
import { fetchPujaCategories } from '../../services/epujaService';

const TABS = [
  { id: 'darshan', label: 'Darshan Booking', icon: 'temple' },
  { id: 'hotel', label: 'Hotel Booking', icon: 'bed-double' },
  { id: 'puja', label: 'Puja Booking', icon: 'diya' },
  { id: 'trip', label: 'Plan a Trip', icon: 'route' },
];
const BASE = { darshan: '/darshan-booking', hotel: '/hotels', puja: '/epuja', trip: '/travel-planner' };

/** Build the destination path for a widget search. Empty values are dropped. */
export function buildSearchPath(tab, values) {
  const qs = Object.entries(values)
    .filter(([, v]) => v !== '' && v != null)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  return qs ? `${BASE[tab]}?${qs}` : BASE[tab];
}

const today = () => new Date().toISOString().slice(0, 10);
const PERSONS = [1, 2, 3, 4, 5, 6, 7, 8];

function WField({ icon, label, caret = true, children }) {
  return (
    <label className="bw__field">
      <Icon name={icon} size={18} className="bw__icon" />
      <span className="sr-only">{label}</span>
      {children}
      {caret && <Icon name="chevron-down" size={16} className="bw__caret" />}
    </label>
  );
}

export default function BookingWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tab, setTab] = useState('darshan');
  const [v, setV] = useState({
    temple: 'ram-mandir', date: today(), persons: 1,
    checkIn: today(), checkOut: '', guests: 2,
    category: '', from: '', days: 2, travelers: 2,
  });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let alive = true;
    fetchPujaCategories()
      .then((c) => alive && setCategories(Array.isArray(c) ? c : []))
      .catch(() => alive && setCategories([]));
    return () => { alive = false; };
  }, []);

  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const picked = {
      darshan: { temple: v.temple, date: v.date, persons: v.persons },
      hotel: { checkIn: v.checkIn, checkOut: v.checkOut, guests: v.guests },
      puja: { category: v.category, date: v.date },
      trip: { from: v.from, date: v.date, days: v.days, travelers: v.travelers },
    }[tab];
    navigate(buildSearchPath(tab, picked));
  };

  const personsSelect = (key) => (
    <WField icon="user" label={t('Persons')}>
      <select value={v[key]} onChange={set(key)}>
        {PERSONS.map((n) => <option key={n} value={n}>{n} {n === 1 ? t('Person') : t('Persons')}</option>)}
      </select>
    </WField>
  );

  return (
    <form className="bw card" onSubmit={submit} aria-label={t('Quick booking')}>
      <Tabs items={TABS.map((x) => ({ ...x, label: t(x.label) }))} value={tab} onChange={setTab} variant="underline" className="bw__tabs" ariaLabel={t('Booking type')} />
      <div className="bw__row">
        <WField icon="map-pin" label={t('Location')}>
          <select value="ayodhya" onChange={() => {}}><option value="ayodhya">Ayodhya Dham</option></select>
        </WField>

        {tab === 'hotel' && (
          <>
            <WField icon="calendar" label={t('Check-in')} caret={false}><input type="date" min={today()} value={v.checkIn} onChange={set('checkIn')} /></WField>
            <WField icon="calendar" label={t('Check-out')} caret={false}><input type="date" min={v.checkIn || today()} value={v.checkOut} onChange={set('checkOut')} /></WField>
            <WField icon="users" label={t('Guests')}>
              <select value={v.guests} onChange={set('guests')}>
                {PERSONS.map((n) => <option key={n} value={n}>{n} {n === 1 ? t('Guest') : t('Guests')}</option>)}
              </select>
            </WField>
          </>
        )}

        {tab === 'puja' && (
          <>
            <WField icon="calendar" label={t('Date')} caret={false}><input type="date" min={today()} value={v.date} onChange={set('date')} /></WField>
            <WField icon="diya" label={t('Puja / Seva')}>
              <select value={v.category} onChange={set('category')}>
                <option value="">{t('Any puja or seva')}</option>
                {categories.map((c) => <option key={c.category} value={c.category}>{c.category}</option>)}
              </select>
            </WField>
            {personsSelect('persons')}
          </>
        )}

        {tab === 'trip' && (
          <>
            <WField icon="navigation" label={t('Arriving from')} caret={false}><input type="text" placeholder={t('Arriving from (city)')} value={v.from} onChange={set('from')} /></WField>
            <WField icon="calendar" label={t('Arrival date')} caret={false}><input type="date" min={today()} value={v.date} onChange={set('date')} /></WField>
            <WField icon="calendar-days" label={t('Duration')}>
              <select value={v.days} onChange={set('days')}>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} {n === 1 ? t('Day') : t('Days')}</option>)}
              </select>
            </WField>
            <WField icon="users" label={t('Travellers')}>
              <select value={v.travelers} onChange={set('travelers')}>
                {PERSONS.map((n) => <option key={n} value={n}>{n} {n === 1 ? t('Traveller') : t('Travellers')}</option>)}
              </select>
            </WField>
          </>
        )}

        {tab === 'darshan' && (
          <>
            <WField icon="calendar" label={t('Date')} caret={false}><input type="date" min={today()} value={v.date} onChange={set('date')} /></WField>
            <WField icon="temple" label={t('Select Temple')}>
              <select value={v.temple} onChange={set('temple')}>
                {DARSHAN_TEMPLES.map((tpl) => <option key={tpl.id} value={tpl.id}>{t(tpl.name)}</option>)}
              </select>
            </WField>
            {personsSelect('persons')}
          </>
        )}

        <Button type="submit" icon={<Icon name="search" size={18} />} iconPosition="left" className="bw__submit">{t('Search')}</Button>
      </div>
    </form>
  );
}
